import React, { useCallback, useEffect, useRef, useState } from "react";
import VimeoPlayer from "@vimeo/player";
import { useIonViewDidEnter, useIonViewWillLeave } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import {
  registerFeedVideo,
  reportFeedVideoVisibility,
  clearFeedVideoVisibility,
  getFeedMuted,
  setFeedMutedFromUser,
  subscribeFeedMuted,
  hasUserGesture,
  whenUserGesture,
} from "../../services/videoFeedCoordinator";
import VideoControls from "../VideoControls";
import WatchOnOtherDeviceSheet from "../WatchOnOtherDeviceSheet";
import useVideoTelemetry from "./useVideoTelemetry";
import { VIDEO_MAX_HEIGHT, pickCapLevel, vimeoEmbedQuality } from "./videoQuality";
import config from "../../settings/config";
import { getAuthToken } from "../../services/authStorage";
import {
  claimVideoPlayback,
  nextVideoPlaybackId,
  releaseVideoPlayback,
} from "../../utils/exclusiveVideoPlayback";
import "./CustomVimeoPlayer.css";

const ORIENTATION_FULLSCREEN_BODY_CLASS = "cvp-orientation-fullscreen-lock";

// 1×1 transparent GIF. Used as the native <video> poster so Android System
// WebView never falls back to its default grey play-button placeholder while a
// video loads — the transparent poster lets the black container show through
// (and a real Vimeo thumbnail replaces it whenever we have one).
const BLANK_POSTER =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// Module-level cache for video metadata (dimensions + thumbnail). Without it,
// every player mount — and every sibling player / revisit — would ask the API
// again. Keyed by video id; an in-flight map dedupes concurrent requests for the
// same id (common on multi-video pages).
const vimeoInfoCache = new Map();
const vimeoInfoInflight = new Map();

// ── App-wide orientation policy ────────────────────────────────────────────
// App.js locks the whole app to portrait. Video pages are the one exception:
// while one is on screen the device must be free to rotate, otherwise the
// rotate-to-full-screen handler never receives an orientation change and 16:9
// videos can only ever be watched letterboxed.
//
// Several players can be mounted at once (the course swiper renders one per
// slide), so the unlock is reference-counted: the first player to ask frees
// rotation, and portrait is restored only once the last one has gone. That is
// what re-locks the app when the user leaves the video page.
let videoOrientationHolders = 0;

const allowRotationForVideoPage = async () => {
  if (!Capacitor.isNativePlatform()) return;
  videoOrientationHolders += 1;
  if (videoOrientationHolders > 1) return;
  try {
    await ScreenOrientation.unlock();
  } catch {
    /* ignore */
  }
};

const restorePortraitForAppPages = async () => {
  if (!Capacitor.isNativePlatform()) return;
  videoOrientationHolders = Math.max(0, videoOrientationHolders - 1);
  if (videoOrientationHolders > 0) return;
  try {
    await ScreenOrientation.lock({ orientation: "portrait" });
  } catch {
    /* ignore */
  }
};

/**
 * Poster and intrinsic size for a Vimeo id, from our own API.
 *
 * This used to call api.vimeo.com straight from the app with an access token
 * written into the bundle: a token able to read the account's private videos,
 * visible to anyone who opened the web app, and one rate-limit bucket shared by
 * every member. The backend already resolves exactly these fields — cached, with
 * the thumbnail at the video's own aspect ratio — for the video popup, so the
 * player asks there instead.
 */
async function fetchVimeoInfo(video) {
  if (vimeoInfoCache.has(video)) return vimeoInfoCache.get(video);
  if (vimeoInfoInflight.has(video)) return vimeoInfoInflight.get(video);

  const request = (async () => {
    const baseUrl = config?.env?.[config?.env?.mode]?.url;
    const token = getAuthToken();

    // Signed-out screens have no token and the endpoint needs one; a poster is
    // not worth a request that can only fail.
    if (!baseUrl || !token) {
      vimeoInfoInflight.delete(video);
      return null;
    }

    const controller = new AbortController();
    // Don't let a slow response keep the poster blank indefinitely.
    const timeout = setTimeout(() => controller.abort(), 6000);
    try {
      const response = await fetch(
        `${baseUrl}/api/videos/vimeo/${encodeURIComponent(video)}/playback`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        },
      );

      // No playable version of this video (yet): nothing to show, not an error.
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`Playback lookup failed: ${response.status}`);

      const data = await response.json();
      const playback = data?.playback ?? null;

      const info = {
        width: playback?.width ?? null,
        height: playback?.height ?? null,
        thumbnail: playback?.thumbnail_url ?? null,
      };

      // Cache successes only; a failed lookup is retried on the next mount.
      if (info.thumbnail || (info.width && info.height)) {
        vimeoInfoCache.set(video, info);
      }

      return info;
    } finally {
      clearTimeout(timeout);
      vimeoInfoInflight.delete(video);
    }
  })();

  vimeoInfoInflight.set(video, request);
  return request;
}

export default function CustomVimeoPlayer({
  video,
  // The Vimeo id used ONLY to fetch the poster image and the video's real
  // dimensions. It exists because `video` does double duty: it also builds the
  // SDK iframe player, so every caller playing an adaptive stream has to pass
  // `video={null}` to keep that iframe from being created. That left the native
  // player with no thumbnail at all, and its poster fell back to a 1x1
  // transparent GIF — a black rectangle with a play button over it, which reads
  // as a broken video rather than one waiting to be started.
  thumbnailVideoId = null,
  // An image to use as the poster instead of the Vimeo thumbnail — a Diretta's
  // cover for its replay, whose own first frame is often black.
  posterUrl = null,
  post_id = 1,
  autoplay = false,
  // When true (community feed), this player joins the feed coordinator:
  // one-at-a-time playback + sound-on-by-default with a remembered mute
  // preference. Only applies to native <video> (videoUrl) — Phase 1 scope.
  // Vimeo-embedded feed videos are unaffected (gated behind the domain
  // whitelist, Phase 2).
  coordinated = false,
  isWatching = null,
  fillParent = false,
  // Community feed (client, 24/09/2026): a video taller than 4:5 (e.g. 9:16)
  // is shown in a 4:5 box, cropped a third from the top and two thirds from
  // the bottom; a tap opens it full screen in its own shape. `feedAspect` is
  // the width/height the feed payload already knows, so the box is right on
  // first paint instead of jumping once the video's own size arrives.
  feedCrop = false,
  feedAspect = null,
  isOpen = true,
  overlay,
  only_post,
  setHasPlayed,
  videoUrl,
  // Adaptive stream for the same video. Preferred over videoUrl when present:
  // the progressive file is one fixed rendition, so a heavy pill (one free
  // course video is 69 MB) simply stalls on a weak connection, while HLS steps
  // down instead. Falls back to videoUrl wherever HLS cannot be played.
  hlsUrl,
  onProgress, // callback(percentWatched: 0..1)
  // Called once when playback reaches the end (both the native and the SDK
  // player). Progress can read ~100% a moment early, so "ended" gets its own
  // signal — the course uses it to move on to the next video-pillola.
  onEnded,
  enableOrientationFullscreen = false,
  showFullscreenButton = false,
  fullscreenButtonMode = "always",
  // When provided, called before user-initiated playback. Return false to block.
  onBeforePlay,
  // Opt in to the app's own controls on the native <video>: "minimal" (feed,
  // auto-hiding), "bar" (videos/recipes) or "course" (bar + completion tick).
  // Left unset, the browser's default controls are used exactly as before.
  controlsVariant = null,
  // 0..1 — where the pill counts as watched, drawn on the seek bar.
  controlsGateAt = null,
  // Whether this player follows the app-wide mute preference. The course opts
  // out: its videos are lesson content the user came to watch, so they always
  // start with sound rather than inheriting a mute set in the feed, and muting
  // one of them stays local instead of silencing the community too.
  sharedMute = true,
  // Highest quality this player may stream, measured on the video's short side
  // (a vertical 720x1280 clip counts as 720p); null lifts the cap. Every player
  // is capped by default: Vimeo serves up to 4K (~23 Mbps), on a phone anything
  // above 720p only costs data and decoding power, and in September 2026 the
  // account went over Vimeo's 2 TB/month bandwidth cap and was suspended.
  // hls.js is capped directly. Native HLS cannot be capped from script, so a
  // capped player takes the progressive file instead (the backend never picks
  // one above 720p), and the Vimeo embed starts at the matching quality.
  hlsMaxHeight = VIDEO_MAX_HEIGHT,
  // Course context for the playback telemetry: { surface, courseId, lessonId,
  // postId, vimeoId, slideIndex, linkExpiresAt }. Only players given one record
  // anything; see useVideoTelemetry.
  telemetry = null,
  // "Guarda su un altro dispositivo" (client, 29/09/2026): { vimeoId, context }
  // for a movement video the backend flagged (`other_device`), where context is
  // { type: "video" | "post", id } or null. Unset: no TV button.
  otherDevice = null,
}) {
  const video_id_sdk = `vimeo-player-sdk-${video}-${post_id}${
    overlay ? "-overlay" : ""
  }${only_post ? "-only_post" : ""}`;

  const sdkPlayerRef = useRef(null);
  const htmlVideoRef = useRef(null);
  const containerRef = useRef(null);
  const orientationFullscreenExitRef = useRef(null);
  const orientationFullscreenCleanupRef = useRef(null);
  // Ref to always hold the latest onProgress callback.
  // This lets the Vimeo SDK timeupdate listener call the latest version
  // WITHOUT needing onProgress in the useEffect dep array — which would
  // destroy and recreate the SDK player on every progress tick.
  const onProgressRef = useRef(onProgress);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  const onBeforePlayRef = useRef(onBeforePlay);
  useEffect(() => {
    onBeforePlayRef.current = onBeforePlay;
  }, [onBeforePlay]);

  // Safari and iOS WKWebView play HLS from a plain <video src>; Android's
  // WebView cannot and needs hls.js driving it.
  //
  // Deliberately NOT used to choose between the two: Chrome answers "maybe" to
  // this MIME type and then fails the element with a format error, so asking it
  // first puts a manifest into src on the one engine that cannot play it. The
  // effect below asks hls.js whether it can run instead, and only falls back to
  // this when it cannot — which is exactly the case on Apple's engines.
  const canPlayHlsNatively = () => {
    try {
      return !!document
        .createElement("video")
        .canPlayType("application/vnd.apple.mpegurl");
    } catch {
      return false;
    }
  };

  // With an adaptive stream in hand the element renders with no src at all and
  // the effect assigns one; setting it here would race whatever it decides.
  const usesHls = !!hlsUrl;

  // Up here rather than with the other playback state: the telemetry below
  // needs it, and must itself come before the effects that depend on it.
  const [isLeavingView, setIsLeavingView] = useState(false);

  // Records what happens to the playback for the course telemetry. Declared
  // ahead of every effect that lists it as a dependency: a dependency array is
  // read during render, so using it any earlier throws.
  const telemetryApi = useVideoTelemetry({
    telemetry,
    htmlVideoRef,
    active: isWatching !== false && !isLeavingView,
    isWatching,
    isLeavingView,
    // Only where every member pause goes through our own controls can a pause
    // nobody asked for be told apart from a tap.
    attributablePauses: !!controlsVariant && !!videoUrl,
    gateAt: controlsGateAt,
    sourceKey: `${hlsUrl || ""}|${videoUrl || ""}|${video || ""}`,
    canFallback: !!videoUrl,
    hasNativeElement: !!videoUrl,
  });

  // The progressive file needs no stream attach, so nothing above reports it.
  useEffect(() => {
    if (videoUrl && !usesHls) telemetryApi.setPath("progressive");
  }, [videoUrl, usesHls, telemetryApi]);

  useEffect(() => {
    const el = htmlVideoRef.current;
    if (!el || !usesHls || isWatching === false) return;

    let hls = null;
    let cancelled = false;

    // Loaded on demand: hls.js is a few hundred KB and only the surfaces that
    // pass an hlsUrl ever need it, so it stays out of the main bundle.
    import("hls.js")
      .then(({ default: Hls }) => {
        if (cancelled || !htmlVideoRef.current) return;

        if (!Hls.isSupported()) {
          // Apple's engines: no MSE, but they play the manifest directly.
          // Anything else without MSE gets the progressive file, which is
          // still better than a player with no source at all.
          const el2 = htmlVideoRef.current;
          // Native HLS picks its own rendition and script cannot cap it, so a
          // capped player takes the progressive file whenever it has one.
          if (canPlayHlsNatively() && !(hlsMaxHeight && videoUrl)) {
            telemetryApi.setPath("hls_native");
            el2.src = hlsUrl;
          } else if (videoUrl) {
            telemetryApi.setPath("progressive");
            el2.src = videoUrl;
          }
          return;
        }

        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          // Keep only the last half minute of what was already played. The
          // default keeps the whole video in memory once watched.
          ...(hlsMaxHeight ? { backBufferLength: 30 } : {}),
        });

        // Registered before the fallback handler below, so a fatal error is
        // recorded before the stream is torn down.
        telemetryApi.setPath(
          window.ManagedMediaSource && Hls.getMediaSource?.() === window.ManagedMediaSource
            ? "hls_js_mms"
            : "hls_js_mse",
        );
        telemetryApi.attachHls(hls, Hls);

        if (hlsMaxHeight) {
          // autoLevelCapping, not capLevelToPlayerSize: the size-based
          // controller rewrites autoLevelCapping on every resize check, which
          // would silently lift this cap again.
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            const cap = pickCapLevel(hls?.levels, hlsMaxHeight);
            if (hls && cap >= 0) hls.autoLevelCapping = cap;
          });
        }
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (!data?.fatal) return;
          // A fatal manifest/network error means this stream is unusable here;
          // drop to the progressive file rather than leaving a dead player.
          //
          // Vimeo answers these manifests with an occasional 5xx — one was
          // caught live on 29 August, a 503 on a Percorso Gratuito pill. hls.js
          // retries, gives up, and lands here. The swap itself always worked;
          // what did not is that the element is left PAUSED AT ZERO, so the
          // member sees a player that simply never starts. That is the "il
          // video non parte" report, and it looked like a connection problem
          // because a reload usually fixes it.
          const el2 = htmlVideoRef.current;
          const wasPlaying = el2 ? !el2.paused : false;

          if (videoUrl) {
            telemetryApi.recordFallback({
              reason: `hls:${data?.type || "unknown"}:${data?.details || "unknown"}`,
              httpStatus: data?.response?.code ?? null,
            });
          }

          try {
            hls.destroy();
          } catch {
            /* already gone */
          }
          hls = null;

          if (!videoUrl || !el2) return;

          el2.src = videoUrl;
          // load() matters: the element still holds the MediaSource hls.js
          // attached, and without it the swap can be ignored entirely.
          try {
            el2.load();
          } catch {
            /* the element is already gone */
          }

          // Only resume what was already running, or what the caller asked to
          // autoplay — never start a video the member had deliberately paused.
          if (wasPlaying || autoplay) {
            const resume = el2.play();
            if (resume?.catch) {
              resume.catch(() => {
                // Blocked without a gesture: fall back to muted, which the
                // autoplay policy always permits, rather than showing nothing.
                el2.muted = true;
                el2.play()?.catch(() => {
                  /* the member can still press play */
                });
              });
            }
          }
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(htmlVideoRef.current);
      })
      .catch(() => {
        if (!cancelled && videoUrl && htmlVideoRef.current) {
          telemetryApi.setPath("progressive");
          htmlVideoRef.current.src = videoUrl;
        }
      });

    return () => {
      cancelled = true;
      try {
        hls?.destroy();
      } catch {
        /* nothing left to tear down */
      }
    };
  }, [usesHls, hlsUrl, videoUrl, isWatching, autoplay, hlsMaxHeight, telemetryApi]);

  // Going dormant has to release the element's source, not just tear down the
  // pipeline. hls.destroy() ends the stream but can leave the blob URL on the
  // <video>, and an element still holding a MediaSource keeps a decoder slot.
  // The course swiper renders EVERY pill of a module at once, so a couple of
  // slides that swiped away can hold on to the small number of simultaneous
  // decoders a mobile WebView allows — and then the pill the member is actually
  // on refuses to start, on that device only.
  //
  // Only for the HLS path: there the element is rendered with no `src` prop, so
  // clearing it by hand cannot fight React's DOM diffing. The progressive path
  // keeps its src prop and is withheld separately when dormant.
  useEffect(() => {
    if (isWatching !== false || !usesHls) return;
    const el = htmlVideoRef.current;
    if (!el) return;

    try {
      telemetryApi.markPauseReason("dormant");
      el.pause();
      el.removeAttribute("src");
      el.load();
    } catch {
      /* the element is already gone */
    }
  }, [isWatching, usesHls, telemetryApi]);

  // Mute choice for players that don't follow the app-wide preference. Starts
  // false — sound — and only a tap on this player's own mute button changes it.
  const localMutedRef = useRef(false);
  // The single question every playback path asks: should this start silent?
  const wantsMuted = () =>
    sharedMute ? getFeedMuted() : localMutedRef.current;

  // setHasPlayed often arrives as an inline/default `() => {}` that changes
  // identity every render. Keep it in a ref so it is NOT a dependency of the
  // SDK-player creation effect — otherwise the player is destroyed & recreated
  // on every parent render, which breaks @vimeo/player's event routing
  // ("Unknown player. Probably unloaded") so play/timeupdate never fire.
  const setHasPlayedRef = useRef(setHasPlayed);
  useEffect(() => {
    setHasPlayedRef.current = setHasPlayed;
  }, [setHasPlayed]);

  const canStartPlayback = () => {
    const gate = onBeforePlayRef.current;
    if (typeof gate !== "function") return true;
    return gate() !== false;
  };

  const [isPlaying, setIsPlaying] = useState(false);
  const [forcePause, setForcePause] = useState(false);
  const [pausedByVisibility, setPausedByVisibility] = useState(false);
  const [isVisibleInViewport, setIsVisibleInViewport] = useState(false);
  const [videoWidth, setVideoWidth] = useState(null);
  const [videoHeight, setVideoHeight] = useState(null);
  // Only wide videos take part in rotation. Square counts as portrait: turning
  // the phone gains it nothing. Null until the dimensions resolve.
  const isLandscapeVideo = !!(
    videoWidth &&
    videoHeight &&
    videoWidth > videoHeight
  );
  const FEED_BOX_RATIO = 4 / 5;
  const knownAspect =
    videoWidth && videoHeight ? videoWidth / videoHeight : feedAspect || null;
  // Unknown size counts as portrait: most feed clips are phone videos.
  const cropsToFeedBox =
    feedCrop && (knownAspect == null || knownAspect < FEED_BOX_RATIO);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  // The Vimeo lookup still runs for the video's dimensions; only the picture
  // is replaced.
  const posterImage = posterUrl || thumbnailUrl;
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [videoFrameReady, setVideoFrameReady] = useState(false);
  const [manualVisualFullscreen, setManualVisualFullscreen] = useState(false);
  // Real (browser) full screen, mirrored into state. Reading the document
  // during render can't work: entering or leaving full screen — via our button,
  // Esc, or the OS back gesture — doesn't re-render on its own, so the toggle
  // would keep showing "expand" while already full screen.
  const [isDocFullscreen, setIsDocFullscreen] = useState(false);
  const [otherDeviceOpen, setOtherDeviceOpen] = useState(false);
  // True while full screen has rotated the device to landscape, so we only
  // restore portrait if we were the ones who changed it.
  const landscapeLockedRef = useRef(false);
  // Delays handing rotation back after a full-screen exit, so the device has
  // finished snapping to portrait before it is free to rotate again.
  const portraitSettleTimerRef = useRef(null);
  const programmaticPauseRef = useRef(false);
  const programmaticPlayRef = useRef(false);

  // Feed coordination applies to the community feed (coordinated). It originally
  // required a native video_url, but feed posts whose video_url is null fall back
  // to the Vimeo SDK iframe — those must join the coordinator too, otherwise they
  // never autoplay (they'd just sit on the black thumbnail). Any feed video with
  // a native url OR a Vimeo id participates.
  const feedAutoplay = coordinated && (!!videoUrl || !!video);
  // True only while THIS player is the most-visible one per the coordinator.
  const [isFeedActive, setIsFeedActive] = useState(false);
  // Flips true once the SDK player's iframe is ready. Feeds the autoplay effect
  // so play() is (re)attempted after the player mounts — otherwise a player that
  // becomes ready AFTER it's already in view/active never receives the play call.
  const [sdkReady, setSdkReady] = useState(false);
  // We deliberately mute/unmute the element in code; this ref lets the
  // volumechange handler tell our programmatic changes from a real user tap.
  const programmaticMuteRef = useRef(false);
  // SDK/Vimeo counterpart of programmaticMuteRef. Starts true so the Vimeo
  // player's initial default volume event — and our own setMuted() calls — are
  // NOT persisted as if the user had changed the sound. Without this, that
  // initial event overwrites a remembered "muted" pref on app restart, so the
  // next video plays with sound.
  const sdkProgrammaticMuteRef = useRef(true);
  // Set when we fell back to muted autoplay but the user's preference is sound:
  // the next user gesture unmutes the playing video.
  const wantsSoundRef = useRef(false);

  const setContainerVisualFullscreen = (active) => {
    const container = containerRef.current;
    container?.classList.toggle("cvp-landscape-fullscreen", active);

    if (container) {
      const fullscreenStyles = {
        position: "fixed",
        inset: "0",
        width: "100vw",
        height: "100vh",
        zIndex: "2147483647",
        background: "#000",
        margin: "0",
        padding: "0",
        borderRadius: "0",
        aspectRatio: "auto",
        overflow: "hidden",
      };

      Object.entries(fullscreenStyles).forEach(([property, value]) => {
        if (active) {
          container.style.setProperty(property, value, "important");
        } else {
          container.style.removeProperty(property);
        }
      });
    }

    document.body?.classList.toggle(ORIENTATION_FULLSCREEN_BODY_CLASS, active);
  };

  // Serializes SDK play/pause calls. The Vimeo SDK rejects new play() calls
  // with "PlayInterrupted" if a previous play() or pause() is still in flight.
  // We always await the in-flight operation before starting a new one.
  const sdkOpChainRef = useRef(Promise.resolve());
  const runSdkOp = (label, fn) => {
    const next = sdkOpChainRef.current
      .catch(() => {}) // swallow errors from prior op so chain continues
      .then(() => fn())
      .catch((e) => {
        // Only log if not the expected PlayInterrupted during rapid transitions
        if (e?.name !== "PlayInterrupted") {
          console.warn(`SDK ${label} rejected:`, e);
        }
      });
    sdkOpChainRef.current = next;
    return next;
  };

  // ── One video at a time, app-wide ──────────────────────────────────────────
  // Starting playback here silences whatever else was playing. The feed already
  // coordinates its own players; this covers everything else — most visibly the
  // course, where each module page and each video-pillola mounts its own
  // autoplaying player and Ionic keeps the page you left mounted, so two videos
  // used to talk over each other.
  const playbackIdRef = useRef(null);
  if (playbackIdRef.current === null) {
    playbackIdRef.current = nextVideoPlaybackId();
  }

  // Held in a ref so the registry always calls the CURRENT closure: the
  // callback is handed over once, at play time, but must still see today's
  // refs when it fires much later.
  const pauseForOtherPlayerRef = useRef(() => {});
  pauseForOtherPlayerRef.current = () => {
    setIsPlaying(false);

    if (feedAutoplay) {
      // A feed video must stay resumable. The feed autoplays whatever scrolls
      // into view, so marking it pausedByVisibility (what the visibility effect
      // itself would do) lets it start again when it becomes the active card;
      // forcePause would silence it for good and it would never autoplay on
      // scroll-back. Exclusivity inside the feed is the coordinator's job
      // anyway — it deactivates the previous card before activating the next —
      // so this path only fires when a player on ANOTHER surface took over.
      setPausedByVisibility(true);
    } else {
      // Everywhere else, suppress this player's own auto-restart: the
      // visibility effect would otherwise notice a paused-but-visible autoplay
      // player and start it again, and the two videos would trade the slot
      // forever. This is the course case — a module page and a video-pillola
      // page both mount autoplaying players and Ionic keeps both alive.
      setForcePause(true);
      setPausedByVisibility(false);
    }

    const el = htmlVideoRef.current;
    if (el && !el.paused) {
      telemetryApi.markPauseReason("exclusive_takeover");
      el.pause();
    }

    if (!videoUrl && sdkPlayerRef.current) {
      programmaticPauseRef.current = true;
      runSdkOp("pause", () => sdkPlayerRef.current.pause());
    }
  };

  const claimExclusivePlayback = () =>
    claimVideoPlayback(playbackIdRef.current, () =>
      pauseForOtherPlayerRef.current(),
    );

  const releaseExclusivePlayback = () =>
    releaseVideoPlayback(playbackIdRef.current);

  // Unmounting must free the slot, or a player that no longer exists stays
  // "active" and the next video has nothing to hand over from.
  useEffect(() => releaseExclusivePlayback, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Grace period during resize/rotation — suppresses visibility-based pauses
  // that would otherwise permanently freeze the video via forcePause.
  const resizingRef = useRef(false);
  const resizeTimerRef = useRef(null);
  useEffect(() => {
    const markResizing = () => {
      resizingRef.current = true;
      clearTimeout(resizeTimerRef.current);
      // 1200ms covers orientation animation + Vimeo SDK fullscreen transition
      // on slower devices. Prevents pause/play races that cause PlayInterrupted.
      resizeTimerRef.current = setTimeout(() => {
        resizingRef.current = false;
      }, 1200);
    };
    window.addEventListener("resize", markResizing);
    window.addEventListener("orientationchange", markResizing);
    // Fullscreen change also triggers layout reflow; treat it like a resize
    // so the pause/play effect doesn't react to intermediate state changes.
    document.addEventListener("fullscreenchange", markResizing);
    document.addEventListener("webkitfullscreenchange", markResizing);
    return () => {
      window.removeEventListener("resize", markResizing);
      window.removeEventListener("orientationchange", markResizing);
      document.removeEventListener("fullscreenchange", markResizing);
      document.removeEventListener("webkitfullscreenchange", markResizing);
      clearTimeout(resizeTimerRef.current);
    };
  }, []);

  // Opt-in Video Detail behavior: physical landscape enters a visual
  // fullscreen state; Vimeo/browser fullscreen exit returns native builds to
  // portrait and cleans up all CSS state.
  useEffect(() => {
    // Rotation is for wide videos only. A 16:9 clip is unwatchable letterboxed
    // in portrait, so it earns the unlock; a vertical 4:5 or 9:16 clip already
    // fills a portrait screen and only gets smaller when the phone turns, so
    // those stay locked and never auto-enter full screen on rotation.
    // Dimensions arrive asynchronously — until they do, isLandscapeVideo is
    // false and the app-wide portrait lock simply stays in force.
    if (!enableOrientationFullscreen || isLeavingView || !isLandscapeVideo) {
      orientationFullscreenExitRef.current = null;
      orientationFullscreenCleanupRef.current = null;
      return;
    }

    const isNative = Capacitor.isNativePlatform();
    const isActiveRef = { current: false };
    const portraitLockedRef = { current: false };
    const lastLandscapeRef = { current: null };
    const orientationTimerRef = { current: null };
    const fullscreenEntryRef = { current: false };
    const fullscreenEntryTimerRef = { current: null };

    const setVisualFullscreen = (active) => {
      isActiveRef.current = active;
      setContainerVisualFullscreen(active);
    };

    const getScreenOrientationType = async () => {
      if (isNative) {
        try {
          const cur = await ScreenOrientation.orientation();
          return cur?.type;
        } catch {
          /* fall through to web APIs */
        }
      }
      return screen.orientation?.type;
    };

    const isLandscapeNow = async (eventOrientation) => {
      const type =
        eventOrientation?.type ??
        eventOrientation?.screenOrientation?.type ??
        eventOrientation?.orientation?.type ??
        (await getScreenOrientationType());

      return (
        type?.startsWith("landscape") ||
        window.matchMedia?.("(orientation: landscape)")?.matches ||
        window.innerWidth > window.innerHeight
      );
    };

    const tryWebFullscreen = async () => {
      if (navigator.userActivation && !navigator.userActivation.isActive) {
        return false;
      }

      const hasDocumentFullscreen = () =>
        Boolean(document.fullscreenElement || document.webkitFullscreenElement);

      const requestElementFullscreen = async (element, request) => {
        try {
          const result = request.call(element);
          if (result && typeof result.then === "function") {
            await result;
          }
        } catch {
          return false;
        }

        return hasDocumentFullscreen();
      };

      const player = sdkPlayerRef.current;
      if (player) {
        try {
          if (player.ready) await player.ready();
          if (player.requestFullscreen) {
            await player.requestFullscreen();
            if (hasDocumentFullscreen()) return true;
          }
        } catch {
          /* fall through */
        }
      }

      const iframe = document
        .getElementById(video_id_sdk)
        ?.querySelector("iframe");
      if (iframe) {
        const request =
          iframe.requestFullscreen ||
          iframe.webkitRequestFullscreen ||
          iframe.webkitEnterFullscreen;
        if (request) {
          if (await requestElementFullscreen(iframe, request)) {
            return true;
          }
        }
      }

      const el = htmlVideoRef.current;
      if (el) {
        const request =
          el.requestFullscreen ||
          el.webkitEnterFullscreen ||
          el.webkitRequestFullScreen;
        if (request) {
          if (await requestElementFullscreen(el, request)) {
            return true;
          }
        }
      }
      return false;
    };

    const tryWebFullscreenExit = async () => {
      const inFullscreen =
        document.fullscreenElement || document.webkitFullscreenElement;

      if (inFullscreen) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        try {
          const result = exit?.call(document);
          if (result && typeof result.then === "function") {
            await result;
          }
          return;
        } catch {
          /* fall through */
        }
      }

      const player = sdkPlayerRef.current;
      if (player?.exitFullscreen) {
        try {
          await player.exitFullscreen();
        } catch {
          /* ignore */
        }
      }
    };

    const pauseForOrientationFullscreen = () => {
      if (videoUrl && htmlVideoRef.current && !htmlVideoRef.current.paused) {
        telemetryApi.markPauseReason("orientation");
        htmlVideoRef.current.pause();
        return;
      }

      if (sdkPlayerRef.current) {
        programmaticPauseRef.current = true;
        runSdkOp("pause for orientation fullscreen", () =>
          sdkPlayerRef.current.pause()
        );
      }
    };

    const enterVisualFullscreen = () => {
      pauseForOrientationFullscreen();
      setVisualFullscreen(true);
    };

    const lockPortraitBestEffort = async () => {
      if (isNative) {
        try {
          await ScreenOrientation.lock({ orientation: "portrait" });
          portraitLockedRef.current = true;
        } catch {
          /* ignore */
        }
        return;
      }

      try {
        await screen.orientation?.lock?.("portrait-primary");
      } catch {
        /* Most browsers reject this outside real fullscreen. */
      }
    };

    const enterFullscreen = async () => {
      if (isActiveRef.current) return;

      if (isNative) {
        enterVisualFullscreen();
        return;
      }

      fullscreenEntryRef.current = true;
      clearTimeout(fullscreenEntryTimerRef.current);
      try {
        const ok = await tryWebFullscreen();
        if (!ok) {
          await enterVisualFullscreen();
        } else {
          isActiveRef.current = true;
          document.body?.classList.add(ORIENTATION_FULLSCREEN_BODY_CLASS);
        }
      } finally {
        fullscreenEntryTimerRef.current = setTimeout(() => {
          fullscreenEntryRef.current = false;
        }, 800);
      }
    };

    const exitFullscreen = async ({ lockPortrait = false } = {}) => {
      if (lockPortrait) {
        await lockPortraitBestEffort();
      }

      if (!isNative) {
        await tryWebFullscreenExit();
      }

      setVisualFullscreen(false);
    };

    const handleOrientation = (eventOrientation) => {
      clearTimeout(orientationTimerRef.current);
      orientationTimerRef.current = setTimeout(async () => {
        const landscape = await isLandscapeNow(eventOrientation);
        if (landscape === lastLandscapeRef.current) return;

        lastLandscapeRef.current = landscape;
        if (landscape) {
          enterFullscreen();
        } else {
          exitFullscreen();
        }
      }, 200);
    };

    const handleExternalFullscreenExit = () => {
      if (!isNative && fullscreenEntryRef.current) return;

      const stillFullscreen =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (!isActiveRef.current && !stillFullscreen) return;

      lastLandscapeRef.current = false;
      exitFullscreen({ lockPortrait: true });
    };

    orientationFullscreenExitRef.current = handleExternalFullscreenExit;

    const handleDocumentFullscreenChange = () => {
      const stillFullscreen =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (!stillFullscreen) handleExternalFullscreenExit();
    };

    window.addEventListener("orientationchange", handleOrientation);
    window.addEventListener("resize", handleOrientation);
    screen.orientation?.addEventListener?.("change", handleOrientation);
    document.addEventListener("fullscreenchange", handleDocumentFullscreenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      handleDocumentFullscreenChange
    );

    let capacitorOrientationHandle = null;
    if (isNative) {
      try {
        const maybePromise = ScreenOrientation.addListener?.(
          "screenOrientationChange",
          handleOrientation
        );
        if (maybePromise && typeof maybePromise.then === "function") {
          maybePromise.then((handle) => {
            capacitorOrientationHandle = handle;
          });
        } else {
          capacitorOrientationHandle = maybePromise;
        }
      } catch {
        /* plugin may not support addListener on this version */
      }
    }

    const cleanup = () => {
      clearTimeout(orientationTimerRef.current);
      clearTimeout(fullscreenEntryTimerRef.current);
      window.removeEventListener("orientationchange", handleOrientation);
      window.removeEventListener("resize", handleOrientation);
      screen.orientation?.removeEventListener?.("change", handleOrientation);
      document.removeEventListener(
        "fullscreenchange",
        handleDocumentFullscreenChange
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        handleDocumentFullscreenChange
      );
      try {
        capacitorOrientationHandle?.remove?.();
      } catch {
        /* ignore */
      }
      if (!isNative) {
        tryWebFullscreenExit();
      }
      setVisualFullscreen(false);
      orientationFullscreenExitRef.current = null;
      orientationFullscreenCleanupRef.current = null;

      if (isNative && portraitLockedRef.current) {
        // App-wide policy is portrait-only; re-lock portrait (rather than
        // release to the sensor) so leaving the video doesn't leave the rest
        // of the app rotatable.
        ScreenOrientation.lock({ orientation: "portrait" }).catch(() => {});
        portraitLockedRef.current = false;
      }
    };

    orientationFullscreenCleanupRef.current = cleanup;

    // Free the device to rotate for as long as this video page is on screen.
    // Without this the app's global portrait lock swallows every orientation
    // change and handleOrientation() below is never called.
    allowRotationForVideoPage();

    handleOrientation();

    return () => {
      cleanup();
      // Back to the app-wide portrait policy once the last video player on
      // this page unmounts — i.e. when the user navigates away.
      restorePortraitForAppPages();
    };
  }, [enableOrientationFullscreen, isLeavingView, video_id_sdk, isLandscapeVideo, telemetryApi]);

  // Join the feed coordinator so only the most-visible feed video plays.
  useEffect(() => {
    if (!feedAutoplay) return;
    const id = video_id_sdk;
    const unregister = registerFeedVideo(id, setIsFeedActive);
    return () => {
      clearFeedVideoVisibility(id);
      unregister();
    };
  }, [feedAutoplay, video_id_sdk]);

  // Keep EVERY video in sync with the shared, persisted mute preference:
  // muting one mutes all current + subsequent ones, and the choice survives
  // navigation and sessions (the coordinator persists it in localStorage).
  // Default, with nothing stored, is sound ON.
  //
  // This deliberately is not limited to the feed: the preference is global, so
  // muting in the community has to carry into the percorso and the video pages
  // too — while it was feed-only, each surface kept its own sound state.
  useEffect(() => {
    // Opted out (course): nothing outside this player may change its sound.
    if (!sharedMute) return undefined;
    const unsubscribe = subscribeFeedMuted((nextMuted) => {
      const el = htmlVideoRef.current;
      if (el) setMutedProgrammatically(el, nextMuted);
      // Also apply to a currently-playing Vimeo SDK video so a mute/unmute on
      // one video type propagates live to the other (vimeo ↔ html, feed ↔ tab).
      const sdk = sdkPlayerRef.current;
      if (sdk) {
        // Guard the volumechange this triggers, then clear it — setMuted() may
        // be a no-op that fires no volumechange, and a stuck guard would swallow
        // the user's next genuine tap (so the following video kept its own
        // sound instead of the shared mute).
        sdkProgrammaticMuteRef.current = true;
        const clearGuard = () => {
          sdkProgrammaticMuteRef.current = false;
        };
        sdk.setMuted(nextMuted).then(clearGuard, clearGuard);
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedMute]);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry.intersectionRatio;
        // Preserve the original "≥50% visible" semantics for the play gate.
        setIsVisibleInViewport(entry.isIntersecting && ratio >= 0.5);
        // Feed videos additionally report their exact ratio so the coordinator
        // can pick the single most-visible one to play.
        if (feedAutoplay) {
          reportFeedVideoVisibility(
            video_id_sdk,
            entry.isIntersecting ? ratio : 0,
          );
        }
      },
      {
        // Multiple thresholds give the coordinator a usable ratio as the user
        // scrolls, not just a binary crossing.
        threshold: feedAutoplay ? [0, 0.25, 0.5, 0.75, 1] : 0.5,
        rootMargin: "0px",
      },
    );

    observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [post_id, feedAutoplay, video_id_sdk]);

  /**
   * Read the real geometry, because the observer's answer goes stale.
   *
   * Swiper — the course pills, the feed carousels — moves between slides with a
   * transform on its wrapper. A transform changes no element's layout box, so
   * the IntersectionObserver above never runs a second time: it reports once, at
   * mount, when every slide except the first is still translated off screen.
   * `isVisibleInViewport` then stays frozen at false for the rest of the module.
   *
   * That froze the percorso. The play gate below pauses any player it believes
   * is off screen, so from the second pill onward the video the member was
   * actually looking at was paused on every render — it never advanced a frame,
   * `onProgress` never fired, the 80% completion gate could never be met, and
   * "Vai alla prossima" stayed disabled with no way forward. Only something that
   * forces a re-layout (rotating the phone, the keyboard opening, a resize) made
   * the observer speak again, which is why it looked intermittent and why the
   * native app — locked to portrait, with no browser chrome to collapse — was
   * where members got stuck.
   */
  const measureViewportVisibility = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (!area) return;

    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const visibleW = Math.max(0, Math.min(rect.right, vw) - Math.max(rect.left, 0));
    const visibleH = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));

    // Same "at least half of it is on screen" rule the observer applies.
    setIsVisibleInViewport((visibleW * visibleH) / area >= 0.5);
  }, []);

  // Becoming — or ceasing to be — the active slide is exactly the moment the
  // observer cannot report. Measured twice: once now, and once after the slide
  // transition has settled, since at the instant the active index flips Swiper
  // is still mid-transform and the box is only part of the way across.
  useEffect(() => {
    if (isWatching === null) return undefined;

    const frame = requestAnimationFrame(measureViewportVisibility);
    const settled = setTimeout(measureViewportVisibility, 450);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settled);
    };
  }, [isWatching, measureViewportVisibility]);

  useEffect(() => {
    // Fetch (cached) whenever a Vimeo id exists — including native video_url
    // posts that also carry one — so we can use the real thumbnail as the poster.
    const posterId = thumbnailVideoId || video;
    if (!posterId) return;

    let cancelled = false;
    fetchVimeoInfo(posterId)
      .then((info) => {
        if (cancelled || !info) return;
        if (info.width && info.height) {
          setVideoWidth(info.width);
          setVideoHeight(info.height);
        }
        if (info.thumbnail) setThumbnailUrl(info.thumbnail);
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Error al obtener información del video (API):", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [video, thumbnailVideoId, videoUrl]);

  useEffect(() => {
    if (videoUrl && htmlVideoRef.current) {
      const videoElement = htmlVideoRef.current;
      const handleMetadata = () => {
        if (videoElement.videoWidth && videoElement.videoHeight) {
          setVideoWidth(videoElement.videoWidth);
          setVideoHeight(videoElement.videoHeight);
        }
      };
      videoElement.addEventListener("loadedmetadata", handleMetadata);
      if (videoElement.readyState >= 1) {
        handleMetadata();
      }
      return () =>
        videoElement.removeEventListener("loadedmetadata", handleMetadata);
    }
  }, [videoUrl]);

  useEffect(() => {
    // Don't mount the Vimeo iframe for players explicitly marked not-watching
    // (e.g. the non-active lesson slides in the course Swiper). Otherwise a
    // module with N lessons loads N full Vimeo players at once — the main cause
    // of "course video pills very slow to load". When a slide becomes active,
    // isWatching flips to true and this effect re-runs to create the player on
    // demand (and the previous slide's cleanup destroys its iframe). Players
    // that never set isWatching (default null) are unaffected.
    if (videoUrl || !video || !isOpen || isWatching === false) return;

    const targetElement = document.getElementById(video_id_sdk);
    if (!targetElement) {
      return;
    }

    let sdkPlayerInstance = null;
    // Tracks whether we've already unmuted after the first successful play.
    // Browsers block unmuted autoplay but allow programmatic unmute once the
    // media has begun playing — so we start muted and unmute on first play.
    let unmutedOnce = false;
    try {
      sdkPlayerInstance = new VimeoPlayer(targetElement, {
        id: parseInt(video, 10),
        // Native muted autoplay (autoplay=1 in the embed) is the only reliable
        // way to start a Vimeo iframe without a user gesture — a post-load
        // programmatic play() is silently blocked by the browser's autoplay
        // policy. Enable it for the coordinated community feed (and for course
        // slides that are the active one). The viewport/coordinator effect still
        // pauses off-screen / non-active players.
        autoplay: autoplay && (coordinated || isWatching === true),
        muted: true, // required for guaranteed autoplay across browsers
        controls: true,
        fullscreen: true,
        // Only where the embed starts: Vimeo offers no hard cap for it.
        ...(hlsMaxHeight ? { quality: vimeoEmbedQuality(hlsMaxHeight) } : {}),
      });
      sdkPlayerRef.current = sdkPlayerInstance;
      telemetryApi.attachSdk(sdkPlayerInstance);

      sdkPlayerInstance.on("play", () => {
        setIsPlaying(true);
        setHasPlayedRef.current?.(true);
        claimExclusivePlayback();

        // user or programmatic play -> allow playing again
        programmaticPlayRef.current = false;
        setForcePause(false);
        setPausedByVisibility(false);

        // Apply the shared, persisted mute preference on first play so audio is
        // consistent across videos (feed ↔ video tab): a video the user muted
        // earlier stays muted; otherwise it plays with sound (default is
        // sound-on). Only once, so we don't fight the user mid-playback.
        if (!unmutedOnce) {
          unmutedOnce = true;
          // Flag so the volumechange this triggers isn't mistaken for a user tap,
          // then clear it once applied — setMuted() may be a no-op that fires no
          // volumechange, and the guard must not swallow the user's first tap.
          sdkProgrammaticMuteRef.current = true;
          const clearGuard = () => {
            sdkProgrammaticMuteRef.current = false;
          };
          sdkPlayerInstance.setMuted(wantsMuted()).then(clearGuard, clearGuard);
        }
      });

      sdkPlayerInstance.on("pause", () => {
        setIsPlaying(false);
        releaseExclusivePlayback();

        // If WE paused it (visibility / leaving view), don't treat as user pause
        if (programmaticPauseRef.current) {
          programmaticPauseRef.current = false;
          return;
        }

        // During resize/rotation the SDK may fire spurious pause events
        // due to iframe reflow — ignore them to prevent permanent freeze.
        if (resizingRef.current) {
          return;
        }

        // User paused via Vimeo controls -> do NOT auto-play again
        setForcePause(true);
        setPausedByVisibility(false);
      });

      // Persist the user's mute/unmute so subsequent videos match it. The
      // pref is idempotent (no-op when unchanged), so applying it on first
      // play then echoing it back here is harmless.
      sdkPlayerInstance.on("volumechange", (data) => {
        // Ignore our own programmatic mute changes and the SDK's initial default
        // volume event — only persist genuine user taps (mirrors the native
        // <video> path's programmaticMuteRef guard).
        if (sdkProgrammaticMuteRef.current) {
          sdkProgrammaticMuteRef.current = false;
          return;
        }
        const muted =
          typeof data?.muted === "boolean"
            ? data.muted
            : Number(data?.volume) === 0;
        if (sharedMute) setFeedMutedFromUser(!!muted);
        else localMutedRef.current = !!muted;
      });

      sdkPlayerInstance.on("ended", () => {
        setIsPlaying(false);
        setForcePause(true);
        setPausedByVisibility(false);
        releaseExclusivePlayback();
        if (onProgressRef.current) onProgressRef.current(1); // 100%
        if (onEndedRef.current) onEndedRef.current();
      });

      // Progress tracking for 80% gate — use ref so this listener
      // never goes stale and the player is not recreated on every tick.
      sdkPlayerInstance.on("timeupdate", ({ percent }) => {
        // timeupdate only fires while the video is actually playing, so it's a
        // reliable backstop to lift the thumbnail overlay even when the "play"
        // event was missed (native autoplay can start before the SDK wires it
        // up). setIsPlaying(true) is a no-op re-render once already playing.
        setIsPlaying(true);
        if (onProgressRef.current) onProgressRef.current(percent ?? 0);
      });
      sdkPlayerInstance.on("error", (errorData) =>
        console.error("Vimeo SDK Error:", errorData),
      );
      sdkPlayerInstance.on("fullscreenchange", ({ fullscreen }) => {
        if (!fullscreen) {
          orientationFullscreenExitRef.current?.();
        }
      });

      sdkPlayerInstance
        .ready()
        .then(() => {
          const sdkGeneratedIframe = sdkPlayerInstance.element;
          if (sdkGeneratedIframe) {
            sdkGeneratedIframe.setAttribute(
              "allow",
              "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen",
            );
            sdkGeneratedIframe.setAttribute("allowfullscreen", "");
            sdkGeneratedIframe.setAttribute("webkitallowfullscreen", "");
            sdkGeneratedIframe.setAttribute("mozallowfullscreen", "");
            // The Vimeo SDK sizes the iframe to the video's own dimensions
            // (e.g. 240x426 for a portrait clip), which leaves black bars inside
            // the aspect-ratio'd container. Force it to fill the container.
            sdkGeneratedIframe.style.width = "100%";
            sdkGeneratedIframe.style.height = "100%";
          }
          // Signal readiness so the autoplay effect re-runs and plays this
          // player if it's already the in-view / coordinator-active one.
          setSdkReady(true);
          // Native autoplay can start the video BEFORE the SDK finishes wiring
          // up its "play" event, so that event is missed and the thumbnail
          // overlay never lifts. Read the real paused state once and sync it.
          sdkPlayerInstance
            .getPaused()
            .then((paused) => {
              if (!paused) {
                setIsPlaying(true);
                setHasPlayedRef.current?.(true);
              }
            })
            .catch(() => {});
        })
        .catch((error) => console.error("Vimeo SDK ready error:", error));
    } catch (error) {
      console.error("Error initializing Vimeo SDK Player:", error);
    }

    return () => {
      setSdkReady(false);
      if (sdkPlayerRef.current) {
        try {
          sdkPlayerRef.current.off("fullscreenchange");
        } catch {
          /* ignore */
        }
        sdkPlayerRef.current
          .destroy()
          .catch((e) => console.warn("Error destroying Vimeo SDK player", e));
        sdkPlayerRef.current = null;
      }
    };
  }, [
    video,
    isOpen,
    videoUrl,
    video_id_sdk,
    autoplay,
    isWatching,
    telemetryApi,
    hlsMaxHeight,
    // onProgress intentionally omitted — accessed via onProgressRef
    // to prevent player recreation on every progress tick.
  ]);

  // Mute the element programmatically while flagging it so the volumechange
  // handler doesn't mistake our change for a user tap.
  const setMutedProgrammatically = (el, muted) => {
    programmaticMuteRef.current = true;
    el.muted = muted;
    // Setting el.muted to a value it ALREADY holds fires no volumechange, so the
    // guard would stay stuck true and swallow the user's next real tap (the SDK
    // path avoids this with .then(clearGuard)). Clear it on a trailing task,
    // which runs AFTER any volumechange this change queued (that handler clears
    // the guard itself), so this only matters for the genuine no-op case.
    setTimeout(() => {
      programmaticMuteRef.current = false;
    }, 0);
  };

  // Start native playback honoring the remembered sound preference. Web
  // browsers block autoplay WITH sound until the user has interacted, so there
  // we autoplay muted and unmute on the next gesture. The native (Capacitor)
  // WebViews are configured to ALLOW unmuted autoplay (iOS: Capacitor sets
  // mediaTypesRequiringUserActionForPlayback=[]; Android: MainActivity sets
  // setMediaPlaybackRequiresUserGesture(false)), so on device the first feed
  // video can start with sound immediately — no gesture required.
  const startNativePlayback = (el) => {
    // Applies to EVERY native player, not just the coordinated feed. A bare
    // el.play() with sound is blocked by browser autoplay policy, so the video
    // silently never starts — the sound-aware path below always gets playback
    // going (muted if it must) and restores sound at the first gesture.
    const unmutedAutoplayAllowed = Capacitor.isNativePlatform();

    const armUnmuteOnGesture = () => {
      wantsSoundRef.current = true;
      whenUserGesture(() => {
        if (wantsSoundRef.current && !wantsMuted() && !el.paused) {
          setMutedProgrammatically(el, false);
        }
        wantsSoundRef.current = false;
      });
    };

    // Preference is muted, or (on web only) we have no gesture yet → muted
    // autoplay (always allowed). If the user actually wants sound, unmute on
    // first interaction. On native, unmuted autoplay is allowed so we fall
    // through to the sound-on path even without a prior gesture.
    if (wantsMuted() || (!hasUserGesture() && !unmutedAutoplayAllowed)) {
      setMutedProgrammatically(el, true);
      el.play().catch((e) => {
        console.warn("feed muted play rejected:", e);
        telemetryApi.recordPlayRejected(e);
      });
      if (!wantsMuted()) armUnmuteOnGesture();
      return;
    }

    // Sound wanted and a gesture already happened → try unmuted, fall back to
    // muted (and unmute on the next gesture) if the browser still blocks it.
    setMutedProgrammatically(el, false);
    el.play().catch(() => {
      setMutedProgrammatically(el, true);
      el.play().catch((e) => telemetryApi.recordPlayRejected(e));
      armUnmuteOnGesture();
    });
  };

  useEffect(() => {
    // During resize/rotation, IntersectionObserver may fire false negatives.
    // Skip system pause to avoid permanently freezing the video.
    if (resizingRef.current) return;

    const isActuallyVisible =
      isVisibleInViewport &&
      (isWatching === null || isWatching === true) &&
      // In the feed, only the coordinator-chosen video is "visible" for
      // playback — this is what enforces one-video-at-a-time.
      (!feedAutoplay || isFeedActive);

    const canPlay = !isLeavingView && isActuallyVisible;
    const shouldBePausedDueToSystem = isLeavingView || !isActuallyVisible;

    if (videoUrl && htmlVideoRef.current) {
      const videoElement = htmlVideoRef.current;
      if (shouldBePausedDueToSystem) {
        if (!videoElement.paused) {
          telemetryApi.markPauseReason(isLeavingView ? "view_leave" : "visibility_gate");
          videoElement.pause();
          if (!isActuallyVisible && !isLeavingView) {
            setPausedByVisibility(true);
          } else {
            setPausedByVisibility(false);
          }
        }
      } else if (canPlay) {
        if (
          videoElement.paused &&
          ((autoplay && !forcePause) || pausedByVisibility)
        ) {
          telemetryApi.markIntent(pausedByVisibility ? "resume" : "autoplay");
          startNativePlayback(videoElement);
        }
      }
    } else if (!videoUrl && sdkPlayerRef.current) {
      const sdkPlayer = sdkPlayerRef.current;

      if (shouldBePausedDueToSystem) {
        if (isPlaying) {
          telemetryApi.markPauseReason(isLeavingView ? "view_leave" : "visibility_gate");
          programmaticPauseRef.current = true;
          runSdkOp("pause", () => sdkPlayer.pause());

          if (!isActuallyVisible && !isLeavingView) {
            setPausedByVisibility(true);
          } else {
            setPausedByVisibility(false);
          }
        }
      } else if (canPlay) {
        if (!isPlaying && !forcePause && (autoplay || pausedByVisibility)) {
          telemetryApi.markIntent("autoplay");
          programmaticPlayRef.current = true;
          runSdkOp("play", () => sdkPlayer.play());
        }
      }
    }
  }, [
    videoUrl,
    isLeavingView,
    isWatching,
    isVisibleInViewport,
    autoplay,
    forcePause,
    isPlaying,
    pausedByVisibility,
    feedAutoplay,
    isFeedActive,
    sdkReady,
    telemetryApi,
  ]);

  useIonViewDidEnter(() => {
    setIsLeavingView(false);
    if (autoplay) {
      setForcePause(false);
    }
    setPausedByVisibility(false);
  });

  useIonViewWillLeave(() => {
    orientationFullscreenCleanupRef.current?.();
    setContainerVisualFullscreen(false);
    setManualVisualFullscreen(false);
    setIsLeavingView(true);
    telemetryApi.markPauseReason("view_leave");
    if (videoUrl && htmlVideoRef.current && !htmlVideoRef.current.paused) {
      htmlVideoRef.current.pause();
      setIsPlaying(false);
      setForcePause(true);
      setPausedByVisibility(false);
      releaseExclusivePlayback();
    } else if (!videoUrl && sdkPlayerRef.current && isPlaying) {
      runSdkOp("pause", () => sdkPlayerRef.current.pause());
      setIsPlaying(false);
      setForcePause(true);
      setPausedByVisibility(false);
      releaseExclusivePlayback();
    }
  });

  // ── Full-screen orientation ────────────────────────────────────────────
  // The app is locked to portrait globally (App.js), so rotating the phone
  // raises no orientation change at all — "rotate to go full screen" can never
  // fire, and 16:9 videos (the physical-activity ones especially) sat
  // letterboxed in a portrait frame. Full screen therefore drives the rotation
  // itself: landscape on the way in, back to portrait on the way out.

  /** Turn the device to landscape. Safe to call when it can't be honoured. */
  const lockLandscapeForFullscreen = async () => {
    // Wide videos only. Turning the phone for a vertical clip shrinks it —
    // full screen on a 4:5 video stays portrait and simply fills the screen.
    if (!isLandscapeVideo) return;

    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "landscape" });
        landscapeLockedRef.current = true;
      } catch {
        /* Device refused (e.g. the OS rotation lock is on) — leave it be. */
      }
      return;
    }

    // Web: the Screen Orientation API only permits a lock while an element is
    // in document full screen, and desktop browsers reject it outright. That's
    // fine — a desktop window has no orientation to change.
    try {
      await window.screen?.orientation?.lock?.("landscape");
      landscapeLockedRef.current = true;
    } catch {
      /* ignore */
    }
  };

  /** Undo the above — but only if we were the ones who changed it. */
  const restoreOrientationAfterFullscreen = async () => {
    if (!landscapeLockedRef.current) return;
    landscapeLockedRef.current = false;

    if (Capacitor.isNativePlatform()) {
      // Snap the UI back to portrait…
      try {
        await ScreenOrientation.lock({ orientation: "portrait" });
      } catch {
        /* ignore */
      }

      // …then, on a video page, hand rotation back once the device has
      // settled. Staying locked here would mean rotate-to-full-screen worked
      // exactly once per visit. Off a video page the lock is the correct
      // resting state, so leave it.
      if (enableOrientationFullscreen) {
        clearTimeout(portraitSettleTimerRef.current);
        portraitSettleTimerRef.current = setTimeout(() => {
          if (videoOrientationHolders > 0) {
            ScreenOrientation.unlock().catch(() => {});
          }
        }, 700);
      }
      return;
    }

    try {
      window.screen?.orientation?.unlock?.();
    } catch {
      /* ignore */
    }
  };

  // Full screen can also end without our button: Esc, the browser's own
  // control, or the OS back gesture. Catch those too, otherwise the phone
  // stays stuck in landscape after the video closes.
  const restoreOrientationRef = useRef(null);
  useEffect(() => {
    restoreOrientationRef.current = restoreOrientationAfterFullscreen;
  }); // no deps — keep the ref pointing at the current closure

  useEffect(() => {
    const onChange = () => {
      const stillFullscreen = Boolean(
        document.fullscreenElement || document.webkitFullscreenElement
      );
      setIsDocFullscreen(stillFullscreen);
      if (!stillFullscreen) {
        restoreOrientationRef.current?.();
      }
    };

    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);

    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      clearTimeout(portraitSettleTimerRef.current);
      // Unmounting while full screen (e.g. navigating away) must not strand
      // the app in landscape.
      restoreOrientationRef.current?.();
    };
  }, []);

  if (!video && !videoUrl) {
    return null;
  }

  const isDocumentFullscreen = () =>
    Boolean(document.fullscreenElement || document.webkitFullscreenElement);

  const requestElementFullscreen = async (element) => {
    if (!element) return false;
    const request =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.webkitRequestFullScreen ||
      element.webkitEnterFullscreen;
    if (!request) return false;

    try {
      const result = request.call(element);
      if (result && typeof result.then === "function") await result;
      return true;
    } catch {
      return false;
    }
  };

  const exitDocumentFullscreen = async () => {
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (!exit) return false;
    try {
      const result = exit.call(document);
      if (result && typeof result.then === "function") await result;
      return true;
    } catch {
      return false;
    }
  };

  const enterManualFullscreen = async () => {
    const enter = async () => {
      // When we draw the chrome, full-screen the CONTAINER, not the <video>.
      // Full-screening the media element hands the browser/OS its own player,
      // so our bar — progress, mute, the 80% completion tick — disappears the
      // moment the user goes full screen. The container carries the video and
      // the overlay together, so the same UI survives the transition.
      if (controlsVariant && videoUrl) {
        if (await requestElementFullscreen(containerRef.current)) return;
        // iOS Safari/WKWebView has no element full screen outside <video>; the
        // native player there is the only real option, and pseudo-fullscreen
        // (which does keep our chrome) is the last resort below.
        if (await requestElementFullscreen(htmlVideoRef.current)) return;
        setContainerVisualFullscreen(true);
        setManualVisualFullscreen(true);
        return;
      }

      const player = sdkPlayerRef.current;
      if (player?.requestFullscreen) {
        try {
          await player.ready?.();
          await player.requestFullscreen();
          if (isDocumentFullscreen()) return;
        } catch {
          /* Try the generated iframe/container below. */
        }
      }

      const iframe = document
        .getElementById(video_id_sdk)
        ?.querySelector("iframe");
      if (await requestElementFullscreen(iframe)) return;
      if (await requestElementFullscreen(htmlVideoRef.current)) return;
      if (await requestElementFullscreen(containerRef.current)) return;

      setContainerVisualFullscreen(true);
      setManualVisualFullscreen(true);
    };

    await enter();
    // After entering, because the web lock is only allowed once an element is
    // actually full screen.
    await lockLandscapeForFullscreen();
  };

  const exitManualFullscreen = async () => {
    if (isDocumentFullscreen()) {
      await exitDocumentFullscreen();
    }
    setContainerVisualFullscreen(false);
    setManualVisualFullscreen(false);
    await restoreOrientationAfterFullscreen();
  };

  const onManualFullscreenClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (manualVisualFullscreen || isDocumentFullscreen()) {
      exitManualFullscreen();
    } else {
      enterManualFullscreen();
    }
  };

  // The TV button pauses the video here (it is about to play elsewhere) and
  // leaves full screen, so the sheet is not hidden behind the player.
  const openOtherDevice = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    try {
      htmlVideoRef.current?.pause();
    } catch (e) {
      /* nothing playing */
    }
    try {
      await sdkPlayerRef.current?.pause?.();
    } catch (e) {
      /* SDK player not ready */
    }
    if (manualVisualFullscreen || isDocumentFullscreen()) {
      await exitManualFullscreen();
    }
    setOtherDeviceOpen(true);
  };

  const hasOtherDevice = Boolean(otherDevice?.vimeoId);

  // Rendered next to the fullscreen control on both player paths; our own
  // controls carry their own TV button, so the standalone one is for the
  // embed / browser-controls path only.
  const renderOtherDevice = (visible = true) =>
    hasOtherDevice ? (
      <>
        {visible && !controlsVariant ? (
          <button
            type="button"
            className="cvp-otherdevice-button"
            onClick={openOtherDevice}
            aria-label="Guarda su un altro dispositivo"
            title="Guarda su un altro dispositivo"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <rect x="3" y="5" width="18" height="12" rx="2" />
              <path d="M8 21h8M12 17v4" />
            </svg>
          </button>
        ) : null}
        <WatchOnOtherDeviceSheet
          isOpen={otherDeviceOpen}
          onClose={() => setOtherDeviceOpen(false)}
          vimeoId={otherDevice.vimeoId}
          context={otherDevice.context ?? null}
        />
      </>
    ) : null;

  const renderFullscreenButton = (visible = true) => (
    <>
      {renderOtherDevice(visible)}
      {renderStandaloneFullscreenButton(visible)}
    </>
  );

  const renderStandaloneFullscreenButton = (visible = true) =>
    // When our own controls are on, they carry the fullscreen toggle — this
    // standalone one would be a second, duplicate button on the same video.
    showFullscreenButton && visible && !controlsVariant ? (
      <button
        type="button"
        className={`cvp-fullscreen-button ${
          fullscreenButtonMode === "playing-only"
            ? "cvp-fullscreen-button--playing"
            : ""
        }`}
        onClick={onManualFullscreenClick}
        aria-label="Apri video a schermo intero"
        title="Schermo intero"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M5 9V5h4M15 5h4v4M19 15v4h-4M9 19H5v-4" />
        </svg>
      </button>
    ) : null;

  const isAnyFullscreen = manualVisualFullscreen || isDocFullscreen;
  // Crop only while inline; full screen shows the whole frame.
  const feedCropActive = cropsToFeedBox && !isAnyFullscreen;
  const FEED_CROP_POSITION = "50% 33%";

  const openFullscreenFromTap = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (isAnyFullscreen || isDocumentFullscreen()) return;
    if (!canStartPlayback()) return;
    enterManualFullscreen();
  };

  const commonWrapperStyle = fillParent
    ? {
        width: "100%",
        height: "100%",
        position: "relative",
      }
    : cropsToFeedBox
    ? {
        width: "100%",
        backgroundColor: "#000",
        position: "relative",
        aspectRatio: "4/5",
        overflow: "hidden",
      }
    : {
        width: "100%",
        backgroundColor: "#000",
        position: "relative",
        aspectRatio:
          videoWidth && videoHeight && videoHeight !== 0
            ? `${videoWidth / videoHeight}`
            : "16/9",
      };
  if (videoUrl) {
    const htmlOverlayVisible = showThumbnail && !isPlaying;

    const handleHtmlPlay = () => {
      if (!canStartPlayback()) return;
      setShowThumbnail(false);
      telemetryApi.markIntent("tap");
      // Feed: the tap that starts the video also opens it full screen.
      if (feedCropActive) enterManualFullscreen();
      if (htmlVideoRef.current) {
        // Seek back to the start so it plays from 0:00 (not from 0.1s)
        htmlVideoRef.current.currentTime = 0;
        // Go through the shared start path rather than a bare play(). The
        // course pages don't autoplay — this tap IS how their videos start —
        // and a raw play() ignores the global mute preference, so a video
        // muted in the feed came back with sound here.
        startNativePlayback(htmlVideoRef.current);
      }
    };

    // Consumers that render several players at once (the course module swiper)
    // mark the off-screen ones isWatching=false. Withhold the source from those
    // — otherwise every slide downloads its video at the same time, which is
    // what made the course pills slow to load. The active slide gets its src
    // back the moment it becomes current. `isWatching == null` means the
    // consumer doesn't manage this, so behave as before.
    const isDormant = isWatching === false;

    // Append #t=0.1 to force Android WebView to load the first frame as a poster
    // (progressive only — an HLS manifest has no such seek trick, and hls.js
    // owns the element's source once it attaches).
    const videoSrc = isDormant
      ? undefined
      : usesHls
        ? undefined
        : videoUrl && !videoUrl.includes("#t=")
          ? `${videoUrl}#t=0.1`
          : videoUrl;

    return (
      <div
        ref={containerRef}
        style={commonWrapperStyle}
        className="media-player"
      >
        <video
          ref={htmlVideoRef}
          src={videoSrc}
          poster={posterImage || BLANK_POSTER}
          preload={isDormant ? "none" : "metadata"}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            ...(feedCropActive
              ? { objectFit: "cover", objectPosition: FEED_CROP_POSITION }
              : {}),
            // Keep video hidden until first frame is loaded to prevent Android's native play button flash
            ...(htmlOverlayVisible && !videoFrameReady
              ? { visibility: "hidden" }
              : {}),
          }}
          playsInline
          controls={!htmlOverlayVisible && !controlsVariant}
          controlsList="nodownload noplaybackrate"
          onLoadedData={() => setVideoFrameReady(true)}
          onPlay={() => {
            if (!canStartPlayback()) {
              htmlVideoRef.current?.pause();
              setShowThumbnail(true);
              setIsPlaying(false);
              return;
            }
            setIsPlaying(true);
            setShowThumbnail(false);
            setHasPlayedRef.current?.(true);
            setForcePause(false);
            setPausedByVisibility(false);
            claimExclusivePlayback();
          }}
          onPause={() => releaseExclusivePlayback()}
          onVolumeChange={(e) => {
            // Ignore mute changes we made ourselves; only persist real user taps
            // so the choice carries to subsequent autoplays.
            if (programmaticMuteRef.current) {
              programmaticMuteRef.current = false;
              return;
            }
            const el = e.target;
            // Where we draw the controls, our own mute button is the ONLY way a
            // user can mute — and it persists the choice itself. So nothing
            // here may write the shared preference: browsers force a muted
            // start when autoplay-with-sound is blocked, and that fallback was
            // being recorded as if the user had chosen it. One blocked autoplay
            // then muted every video everywhere, for good — which is why videos
            // "start without sound by default".
            if (controlsVariant) return;
            // Course players keep their choice to themselves.
            if (sharedMute) setFeedMutedFromUser(el.muted);
            else localMutedRef.current = el.muted;
            if (!el.muted) wantsSoundRef.current = false;
          }}
          onTimeUpdate={(e) => {
            if (onProgress) {
              const el = e.target;
              const pct = el.duration ? el.currentTime / el.duration : 0;
              onProgress(pct);
            }
          }}
          onEnded={() => {
            setIsPlaying(false);
            setForcePause(true);
            setPausedByVisibility(false);
            releaseExclusivePlayback();
            if (onProgress) onProgress(1);
            if (onEndedRef.current) onEndedRef.current();
          }}
        />
        {/* The app's own controls, when a surface opts in. Hidden while the
            thumbnail overlay is up — that overlay owns the first tap. */}
        {controlsVariant ? (
          <VideoControls
            videoRef={htmlVideoRef}
            onUserAction={telemetryApi.onUserAction}
            variant={controlsVariant}
            gateAt={controlsGateAt}
            hidden={htmlOverlayVisible}
            sharedMute={sharedMute}
            isFullscreen={manualVisualFullscreen || isDocFullscreen}
            // Every layout gets the toggle. `showFullscreenButton` gates the
            // standalone button a surface had to opt into; our own controls are
            // the chrome, and a player whose bar is missing full screen on some
            // pages and not others just reads as a bug.
            onToggleFullscreen={onManualFullscreenClick}
            onOtherDevice={hasOtherDevice ? openOtherDevice : undefined}
            // Controls faded out: the tap lands on them, not on the catcher.
            onSurfaceTap={feedCropActive ? openFullscreenFromTap : undefined}
          />
        ) : null}

        {/* Feed: a tap on the video (not on a control) opens it full screen. */}
        {feedCropActive && !htmlOverlayVisible ? (
          <div
            className="cvp-feed-tap"
            onClick={openFullscreenFromTap}
            aria-hidden="true"
            style={{ position: "absolute", inset: 0, zIndex: 4, cursor: "pointer" }}
          />
        ) : null}

        {/* Custom play overlay:
            - Before first frame loads: dark background + play button (hides Android native controls)
            - After first frame loads: transparent overlay, video first frame shows through as thumbnail
        */}
        {htmlOverlayVisible && (
          <div
            onClick={handleHtmlPlay}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              cursor: "pointer",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              // Dark background while video frame is loading, transparent once ready
              backgroundColor: videoFrameReady ? "transparent" : "#000",
            }}
          >
            {/* The video's own cover, shown until its first frame is decoded.
                Without it this overlay is a flat black rectangle — briefly for a
                video that is about to play, and PERMANENTLY for one that never
                will: a locked course module is rendered with isWatching={false},
                so the element gets no src, `onLoadedData` never fires and
                `videoFrameReady` stays false forever. That was the black preview
                on a not-yet-available module. The SDK branch below already draws
                its thumbnail this way; this makes the two paths agree. */}
            {!videoFrameReady && posterImage && (
              <img
                src={posterImage}
                alt=""
                loading="eager"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  ...(feedCropActive ? { objectPosition: FEED_CROP_POSITION } : {}),
                  display: "block",
                }}
              />
            )}
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // Above the cover image.
                position: "relative",
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}
        {renderFullscreenButton(
          fullscreenButtonMode === "playing-only"
            ? isPlaying || manualVisualFullscreen
            : fullscreenButtonMode !== "overlay-only" ||
                htmlOverlayVisible ||
                manualVisualFullscreen,
        )}
      </div>
    );
  }

  const handleThumbnailClick = () => {
    if (!canStartPlayback()) return;
    setShowThumbnail(false);
    telemetryApi.markIntent("tap");
    if (sdkPlayerRef.current) {
      runSdkOp("play", () => sdkPlayerRef.current.play());
    }
  };

  // Coordinated feed videos autoplay natively (muted), so the blocking
  // thumbnail + play-button overlay must not sit on top of an already-playing
  // clip — it would look paused. Suppress it for the feed; the standalone /
  // course usages keep the tap-to-play overlay.
  const overlayVisible =
    showThumbnail && !isPlaying && !(coordinated && autoplay);

  return (
    <div ref={containerRef} style={{ ...commonWrapperStyle, overflow: "hidden" }}>
      {/* Hide the SDK iframe while overlay is active to prevent Android WebView z-index bleed-through */}
      <div
        id={video_id_sdk}
        style={{
          width: "100%",
          height: "100%",
          // An iframe can't be object-fit: size it to the video's own shape
          // and slide it up by a third of the overflow instead.
          ...(feedCropActive && knownAspect
            ? (() => {
                const scale = FEED_BOX_RATIO / knownAspect;
                return {
                  position: "absolute",
                  left: 0,
                  height: `${scale * 100}%`,
                  top: `${-(scale - 1) * 33}%`,
                };
              })()
            : {}),
          ...(overlayVisible
            ? { opacity: 0, pointerEvents: "none" }
            : {}),
        }}
      />
      {/* Feed: Vimeo's own controls are cropped away here, so a tap opens
          the video full screen instead. */}
      {feedCropActive && !overlayVisible ? (
        <div
          className="cvp-feed-tap"
          onClick={openFullscreenFromTap}
          aria-hidden="true"
          style={{ position: "absolute", inset: 0, zIndex: 3, cursor: "pointer" }}
        />
      ) : null}
      {overlayVisible && (
        <div
          onClick={handleThumbnailClick}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            cursor: "pointer",
            zIndex: 2,
            backgroundColor: "#000",
          }}
        >
          {posterImage && (
            <img
              src={posterImage}
              alt=""
              loading="eager"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                ...(feedCropActive ? { objectPosition: FEED_CROP_POSITION } : {}),
                display: "block",
              }}
            />
          )}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}
      {renderFullscreenButton(
        fullscreenButtonMode === "playing-only"
          ? isPlaying || manualVisualFullscreen
          : fullscreenButtonMode !== "overlay-only" ||
              overlayVisible ||
              manualVisualFullscreen,
      )}
    </div>
  );
}
